/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

/** Free / consumer mailbox providers — not useful for Account linking by domain. */
const GENERIC_EMAIL_DOMAINS = new Set([
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'yahoo.de',
    'hotmail.com',
    'hotmail.de',
    'outlook.com',
    'outlook.de',
    'live.com',
    'msn.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'protonmail.com',
    'proton.me',
    'gmx.com',
    'gmx.de',
    'gmx.ch',
    'gmx.net',
    'web.de',
    'mail.com',
    'yandex.com',
    'zoho.com',
])

/**
 * Extract normalized email domain (e.g. `svasg.ch` from `Selina.Ammann@svasg.ch`).
 */
export const extractEmailDomain = (email?: string | null): string | null => {
    if (!email) {
        return null
    }

    const trimmed = String(email).trim().toLowerCase()
    const atIndex = trimmed.lastIndexOf('@')
    if (atIndex < 0 || atIndex === trimmed.length - 1) {
        return null
    }

    const domain = trimmed.slice(atIndex + 1).replace(/\.+$/, '')
    if (!domain || !domain.includes('.')) {
        return null
    }

    return domain
}

export const isGenericEmailDomain = (domain?: string | null): boolean => {
    if (!domain) {
        return false
    }
    return GENERIC_EMAIL_DOMAINS.has(domain.trim().toLowerCase())
}

/** Business / org domains only — skips gmail etc. */
export const extractBusinessEmailDomain = (email?: string | null): string | null => {
    const domain = extractEmailDomain(email)
    if (!domain || isGenericEmailDomain(domain)) {
        return null
    }
    return domain
}

/** Hostname-like token from website URL (`https://www.svasg.ch/path` → `svasg.ch`). */
export const extractWebsiteDomain = (website?: string | null): string | null => {
    if (!website) {
        return null
    }

    const raw = String(website).trim().toLowerCase()
    if (!raw) {
        return null
    }

    try {
        const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
        const host = new URL(withProtocol).hostname.replace(/^www\./, '')
        return host.includes('.') ? host : null
    } catch {
        const cleaned = raw
            .replace(/^https?:\/\//i, '')
            .replace(/^www\./i, '')
            .split(/[/?#]/)[0]
        return cleaned.includes('.') ? cleaned : null
    }
}

export const domainsMatch = (a?: string | null, b?: string | null): boolean => {
    if (!a || !b) {
        return false
    }
    return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Search token for autocomplete when linking by domain.
 * Prefers the registrable label (`svasg` from `svasg.ch`) so name search can hit "SVA…".
 */
export const getEmailDomainSearchToken = (email?: string | null): string | null => {
    const domain = extractBusinessEmailDomain(email)
    if (!domain) {
        return null
    }

    const label = domain.split('.')[0]
    return label && label.length >= 2 ? label : domain
}
